import { Immutable } from "immer";
import React from "react";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import type { IBaseComponent } from "../../models/formFields";
import { FormAdaText } from "../FormAdaText";
import { FormHelpText } from "../FormHelpText";
import { FormLabel, type LabelPlacement } from "../FormLabel";
import { FormTooltip } from "../FormTooltip";
import { FormValidationError } from "../FormValidationError";
import { FormWarning } from "../FormWarning";
import {
    FORM_FIELD_CHECKBOX_STYLE,
    FORM_FIELD_CLASS_NAME,
    FORM_FIELD_FOCUSED_CLASS_NAME,
    FORM_FIELD_HAS_ERRORS_CLASS_NAME,
    FORM_FIELD_NOT_BLANK_CLASS_NAME,
    FORM_FIELD_RADIO_CHOICE_STYLE,
    FORM_FIELD_REQUIRED_CLASS_NAME,
} from "../classNames";
import { showValidationErrors } from "../hooks/formUtils";

import styles from "../../styles/composable/forms.module.scss";

/**
 * Input types with styling of their own in the forms module (two of them
 * only in a site variant).
 */
const TYPE_STYLES: Readonly<Record<string, string | undefined>> = {
    "checkbox": FORM_FIELD_CHECKBOX_STYLE,
    "fuzzy-duration": styles.formFieldFuzzyDuration,
    "radio": styles.formFieldRadio,
    "radio-choice": FORM_FIELD_RADIO_CHOICE_STYLE,
};

/**
 * Full wrapper for form input fields
 *
 * The literal `form__field*` classes stay alongside the module classes while
 * legacy contextual rules (`.checkout .form__field`, `.form__field--<name>`)
 * still target them (#34958). The `form__field--<name>` and
 * `form__field--<type>` hooks are unbounded and stay literal-only.
 */
export const FormInputFieldWrapper = (
    props: Immutable<
        IBaseComponent & {
            name: string;
            type?: string;
            value?: string | number | boolean | null;
            label?: string;
            placeholder?: string;
            labelCSSClass?: string;
            labelPlacement?: LabelPlacement;
            onLabelClick?: () => void;
            adaText?: string;
            wrapperCSSClass?: string;
            focused?: boolean;
            isRequired: boolean;
            children: React.ReactNode;
        }
    >,
): React.ReactNode => {
    const hasErrors = showValidationErrors(props);
    const classes = concatClassNames(
        props.wrapperCSSClass
            ? [
                  props.wrapperCSSClass,
                  hasErrors ? FORM_FIELD_HAS_ERRORS_CLASS_NAME : null,
              ]
            : [
                  FORM_FIELD_CLASS_NAME,
                  props.isRequired
                      ? FORM_FIELD_REQUIRED_CLASS_NAME
                      : "form__field--optional",
                  props.type ? TYPE_STYLES[props.type] : null,
                  props.type ? `form__field--${props.type}` : null,
                  props.name ? `form__field--${props.name}` : null,
                  props.focused ? FORM_FIELD_FOCUSED_CLASS_NAME : null,
                  props.value ? FORM_FIELD_NOT_BLANK_CLASS_NAME : null,
                  hasErrors ? FORM_FIELD_HAS_ERRORS_CLASS_NAME : null,
              ],
    );
    return (
        // data-testid: stable hook for the Django SSR tests, which must not
        // select by style classnames.
        <div className={classes} data-testid="form-field">
            {(props.label || props.placeholder) && (
                <FormLabel
                    id={props.id}
                    label={props.label}
                    placeholder={props.placeholder}
                    labelCSSClass={props.labelCSSClass}
                    labelPlacement={props.labelPlacement}
                    onLabelClick={props.onLabelClick}
                />
            )}
            {props.tooltipText && (
                <FormTooltip
                    tooltipText={props.tooltipText}
                    tooltipLink={props.tooltipLink}
                    tooltipClass={props.tooltipClass}
                    isIconTrigger={props.isIconTrigger}
                />
            )}
            {props.children}
            <FormValidationError {...props} id={props.id} />
            {props.helpText && (
                <FormHelpText
                    id={props.id}
                    helpText={props.helpText}
                    helpTextClass={props.helpTextClass}
                />
            )}
            {props.warningText && (
                <FormWarning
                    warningTitle={props.warningTitle}
                    warningText={props.warningText}
                />
            )}
            {props.adaText && (
                <FormAdaText id={props.id} adaText={props.adaText} />
            )}
        </div>
    );
};
